// The battle HUD. Battles happen in an arena (a small zone of their own) drawn by the world renderer, so this is
// only what sits on top: the creature you're aiming at, your party's health and time bars, your own bar and the
// six-slot action bar, the Skills and Items lists, banners for what's happening, and the "Encounter!" moment
// that leads in. Everything that happens comes from the server as events; this turns them into numbers, sounds
// and spell effects drawn at whoever they happened to.
//
// Controls: move as usual. When your bar is full, 1–6 (or the d-pad / face buttons on a controller) use the
// bar: Attack, Cure, Fire, Frost, Thunder, Protect. Tab / bumpers pick the creature you're aiming at (or click
// it). Q opens your job's Skills, F your Items, G defends. Walk into the glowing gate to get away.
import { conOf } from '../../../shared/difficulty.js';
import { ATB_FULL, formationDef, type BattleEvent, type BattleView, type Fighter } from '../../../shared/battle.js';
import { abilitiesFor, abilityDef, jobDef, STATUS, barFor, costFor, type AbilityDef, type Element, type JobId } from '../../../shared/jobs.js';
import { MOBS } from '../../../shared/mobs.js';
import { itemDef } from '../../../shared/items.js';
import ink from '../ink/ink.js';
import type { SpellFx } from '../render/spellfx.js';
import type { Sfx } from '../audio/audio.js';

const $ = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;
const I = ink as any;
export interface BattleHost {
  me(): number;
  /** Battle items in your bag (potions, ethers) and how many. */
  items(): { def: string; qty: number }[];
  send(cmd: string, target?: number, skill?: string): void;
  sound(name: Sfx, pitch?: number): void;
  /** The world as it is on screen right now, and where you are on it (canvas pixels), for the intro. */
  snapshot(): { cv: HTMLCanvasElement; x: number; y: number } | null;
  /** Show an effect, a floating number, a flash or a fall on whoever it happened to (drawn in the world). */
  fx(who: number, fx: SpellFx): void;
  num(who: number, text: string, color: string, big?: boolean): void;
  flash(who: number): void;
  down(who: number): void;
  /** Wait mode (solo: the fight holds while your bar is full): what the player picked. */
  waitPref(): boolean;
  setWaitPref(on: boolean): void;
  /** Change the music: the fight's song, back to the town tune, or null to let the current one fade out. */
  song(id: 'town' | 'battle' | 'boss' | null): void;
  /** Targeting: the order Tab goes in, where someone is (arena pixels), and marking a target for the party. */
  tabOrder(): 'near' | 'low' | 'mark';
  pos(id: number): { x: number; y: number } | null;
  markTarget(id: number): void;
  /** Impact: shake the screen (pixels, ms); a big creature dropping in. */
  shake(amp: number, ms: number): void;
  land(who: number): void;
}
const INTRO_MS = 700, INTRO_FADE_MS = 380;
/** What each job calls its ability menu. */
const MENU_NAME: Record<string, string> = { adventurer: 'Skills', knight: 'Skills', wizard: 'Black Magic', cleric: 'White Magic', rogue: 'Tricks', brawler: 'Techniques' };
const EL_COLOR: Record<Element, string> = { fire: '#e8743a', ice: '#4f9ccc', thunder: '#d4a72c', light: '#c99a2e', dark: '#7a5aa8' };
const EL_SOUND: Record<Element, Sfx> = { fire: 'fire', ice: 'ice', thunder: 'thunder', light: 'holy', dark: 'debuff' };
const STATUS_FX: Partial<Record<string, SpellFx>> = { sleep: 'sleep', poison: 'poison', protect: 'protect', regen: 'regen', haste: 'haste', slow: 'ice', provoke: 'shout', sentinel: 'shield', blind: 'smoke', stun: 'bash' };
const ICON: Record<string, string> = { attack: 'sword', cure: 'heal', fire: 'fire', blizzard: 'crystal', thunder: 'bolt', protect: 'shield' };
/** An icon for any ability, from its effect. */
const ICON_FX: Record<string, string> = { fire: 'fire', ice: 'crystal', thunder: 'bolt', heal: 'heal', protect: 'shield', shield: 'shield', light: 'crystal', slash: 'sword', sweep: 'sword', shout: 'crown', steal: 'hat', poison: 'cross', haste: 'bolt', regen: 'heal', smoke: 'hat', sleep: 'hat', raise: 'cross' };
function fxOf(skill: string | undefined): SpellFx | null {
  if (!skill) return null;
  if (skill === 'rocks' || skill === 'quake' || skill === 'gust') return 'bash';
  if (skill === 'ring') return 'drain';
  const a = abilityDef(skill); if (a) return a.fx;
  for (const m of Object.values(MOBS)) { const mv = m.moves?.find(x => x.id === skill) ?? m.phases?.flatMap(p => p.moves ?? []).find(x => x.id === skill); if (mv) return mv.fx === 'breath' ? 'fire' : mv.fx; }
  return null;
}

export class BattleScreen {
  active = false;
  /** The latest view of the fight. */
  b: BattleView | null = null;
  /** The creature you're aiming at. */
  aim: number | null = null;
  private banner = ''; private bannerUntil = 0; private bannerWarn = false;
  private menu: 'skill' | 'item' | 'target-foe' | 'target-ally' | 'target-fallen' | null = null;
  private pending: { cmd: string; skill?: string } | null = null;
  private wasReady = false;
  private ending: { result: string; text: string; at: number } | null = null;
  private resultShown = false;
  onClose: () => void = () => {};

  constructor(private host: BattleHost) {
    $('bHelp').addEventListener('click', () => this.host.send('help'));
    $('bAuto').addEventListener('click', () => this.host.send('auto'));
    $('bWait').addEventListener('click', () => { const on = !this.host.waitPref(); this.host.setWaitPref(on); this.host.send('wait', undefined, on ? 'on' : 'off'); this.tools(); });
    $('bLeave').addEventListener('click', () => this.host.send('leave'));
    $('bSkills').addEventListener('click', () => this.openMenu(this.menu === 'skill' ? null : 'skill'));
    $('bItems').addEventListener('click', () => this.openMenu(this.menu === 'item' ? null : 'item'));
    $('bDefend').addEventListener('click', () => this.command('defend'));
    addEventListener('keydown', e => {
      if (!this.active || /INPUT|TEXTAREA/.test((document.activeElement as HTMLElement)?.tagName || '')) return;
      if (e.code === 'Escape' && this.menu) { e.preventDefault(); this.openMenu(null); return; }
      if (e.code === 'Tab') { e.preventDefault(); this.cycleAim(e.shiftKey ? -1 : 1); return; }
      if (e.code === 'KeyQ') { e.preventDefault(); this.openMenu(this.menu === 'skill' ? null : 'skill'); return; }
      if (e.code === 'KeyF') { e.preventDefault(); e.stopPropagation(); this.openMenu(this.menu === 'item' ? null : 'item'); return; }
      if (e.code === 'KeyG') { e.preventDefault(); this.command('defend'); return; }
      if (e.code === 'KeyT' && this.aim != null) { e.preventDefault(); this.host.markTarget(this.aim); return; }   // leaders: mark it for the party
      const n = Number(e.key);
      if (this.menu && n >= 1 && n <= 9) { const btns = [...$('bMenuList').querySelectorAll<HTMLButtonElement>('button:not([disabled])')]; if (btns[n - 1]) { e.preventDefault(); btns[n - 1].click(); } return; }
      if (n >= 1 && n <= 6) { e.preventDefault(); this.useBar(n - 1); }
    }, true);
  }

  /** Attack (J / X / the attack button / a tap on a creature): at whoever you're aiming at. */
  attack(target?: number): void { if (target != null) this.aim = target; this.useBar(0); }
  /** Aim at a creature (a click or tap on it). */
  setAim(id: number): void { if (this.b?.fighters.some(f => f.id === id && f.side === 'foe' && f.hp > 0)) { this.aim = id; this.panels(); } }
  /** A controller button, in battle: true if it was used. */
  pad(btn: string): boolean {
    if (!this.active || this.ending) return false;
    if (btn === 'lb' || btn === 'rb') { this.cycleAim(btn === 'rb' ? 1 : -1); return true; }
    if (this.menu && btn === 'b') { this.openMenu(null); return true; }
    if (this.menu && btn === 'a') { (document.activeElement as HTMLButtonElement)?.click?.(); return true; }
    if (this.menu && (btn === 'up' || btn === 'down')) { this.focusStep(btn === 'down' ? 1 : -1); return true; }
    const slot = ({ x: 0, up: 1, right: 2, down: 3, left: 4, y: 5 } as Record<string, number>)[btn];
    if (slot != null) { this.useBar(slot); return true; }
    if (btn === 'start') { this.openMenu(this.menu === 'skill' ? null : 'skill'); return true; }
    return false;
  }
  private focusStep(d: number): void {
    const btns = [...$('bMenuList').querySelectorAll<HTMLButtonElement>('button:not([disabled])')]; if (!btns.length) return;
    const i = btns.indexOf(document.activeElement as HTMLButtonElement); btns[(i + d + btns.length) % btns.length].focus();
  }

  private get meF(): Fighter | undefined { return this.b?.fighters.find(f => f.id === this.host.me()); }
  get ready(): boolean { return !!this.b?.ready.includes(this.host.me()) && !this.ending; }
  /** Pick the next creature to aim at, round and round. */
  /** Tab (or LB/RB): the next creature, in the order you set (nearest, weakest, or the leader's mark first). */
  private cycleAim(d: number): void {
    let foes = (this.b?.fighters ?? []).filter(f => f.side === 'foe' && f.hp > 0); if (!foes.length) return;
    const order = this.host.tabOrder(), mePos = this.host.pos(this.host.me());
    const dist = (id: number) => { const p = this.host.pos(id); return p && mePos ? Math.hypot(p.x - mePos.x, p.y - mePos.y) : 0; };
    foes = foes.slice().sort((a, b) => order === 'low' ? a.hp / a.max - b.hp / b.max : dist(a.id) - dist(b.id));
    if (order === 'mark' && this.mark != null) { const m = foes.findIndex(f => f.id === this.mark); if (m > 0) foes.unshift(...foes.splice(m, 1)); }
    const i = foes.findIndex(f => f.id === this.aim);
    this.aim = foes[i < 0 ? 0 : (i + d + foes.length) % foes.length].id; this.host.sound('click'); this.panels();
  }
  /** The party leader's marked target (everyone sees it; "marked target first" Tab jumps there). */
  mark: number | null = null;
  setMark(id: number | null): void { this.mark = id; if (id != null && this.host.tabOrder() === 'mark' && this.b?.fighters.some(f => f.id === id && f.hp > 0)) this.aim = id; this.panels(); }
  private fixAim(): void {
    const foes = (this.b?.fighters ?? []).filter(f => f.side === 'foe' && f.hp > 0);
    if (!foes.some(f => f.id === this.aim)) this.aim = foes[0]?.id ?? null;
  }
  /** Is there a boss in this fight (no gate out)? */
  get boss(): boolean { return !!this.b?.fighters.some(f => f.side === 'foe' && f.mob && MOBS[f.mob]?.boss); }
  /** A fighter as the latest view has them. */
  fighter(id: number): Fighter | undefined { return this.b?.fighters.find(f => f.id === id); }

  /** New state from the server, with what just happened. */
  update(m: { battle: BattleView; events: BattleEvent[]; start?: boolean }): void {
    const fresh = !this.active; if (fresh) this.open();
    this.b = m.battle; this.fixAim();
    if (fresh) this.host.song(this.boss ? 'boss' : 'battle');
    for (const e of m.events) this.play(e);
    const ready = this.ready;
    if (ready && !this.wasReady) this.host.sound('open');
    this.wasReady = ready;
    this.panels(); this.renderBar(); this.tools();
    if (this.menu && !ready) this.openMenu(null);
  }

  end(m: { result: string; text: string }): void {
    this.ending = { result: m.result, text: m.text, at: performance.now() };
    this.openMenu(null); this.renderBar();
    if (m.result === 'left') { this.close(); return; }
    this.resultShown = false;
  }
  /** The fight is over: a short victory moment in the arena, then straight back to the field. The result stays up
   *  as a little card that fades by itself (nothing to press). */
  private showResult(): void {
    const m = this.ending!; this.resultShown = true;
    const r = $('bToast'); r.className = 'b-toast ' + m.result; r.replaceChildren();
    const t = document.createElement('b'); t.textContent = m.text; r.append(t);
    if (m.result !== 'win') { const s = document.createElement('small'); s.textContent = m.result === 'fled' ? 'Safe for now' : 'You wake up by the wayshrine…'; r.append(s); }
    r.hidden = false; void r.offsetWidth; r.classList.add('show');
    clearTimeout(this.toastT); this.toastT = window.setTimeout(() => { r.hidden = true; }, 4200);
    this.host.sound(m.result === 'win' ? 'fanfare' : m.result === 'fled' ? 'dodge' : 'close');
    this.host.song(m.result === 'win' ? null : 'town');
    setTimeout(() => { if (this.ending) this.close(); }, m.result === 'win' ? 1400 : 1000);
  }
  private toastT = 0;

  private open(): void {
    this.active = true; this.ending = null; this.banner = ''; this.aim = null; this.wasReady = false;
    $('bResult').hidden = true; document.body.classList.add('in-battle');
    const snap = this.host.snapshot();
    if (snap) { this.intro = { ...snap, t0: performance.now() }; const fx = $<HTMLCanvasElement>('battleFx'); fx.width = snap.cv.width; fx.height = snap.cv.height; fx.hidden = false; }
    $('battle').hidden = false;
    this.host.sound('battle');
  }
  private intro: { cv: HTMLCanvasElement; x: number; y: number; t0: number } | null = null;
  private drawIntro(now: number): void {
    const it = this.intro!, fx = $<HTMLCanvasElement>('battleFx'), c = fx.getContext('2d')!, W2 = fx.width, H2 = fx.height;
    const t = Math.max(0, now - it.t0);
    if (t >= INTRO_MS + INTRO_FADE_MS) { fx.hidden = true; this.intro = null; return; }
    c.setTransform(1, 0, 0, 1, 0, 0);
    if (t < INTRO_MS) {
      // "Encounter!": the world holds still a moment, then swirls in on you while ink splashes over it
      const k = t / INTRO_MS, hold = Math.min(1, t / 160), z = k < 0.2 ? 1 : 1 + ((k - 0.2) / 0.8) ** 2 * 1.3, turn = k < 0.2 ? 0 : ((k - 0.2) / 0.8) ** 2 * 0.9;
      c.fillStyle = '#fbf6ea'; c.fillRect(0, 0, W2, H2);
      c.save(); c.translate(it.x, it.y); c.rotate(turn); c.scale(z, z); c.translate(-it.x, -it.y); c.drawImage(it.cv, 0, 0); c.restore();
      if (k < 0.35) {
        c.save(); c.globalAlpha = hold * (1 - k / 0.35); c.strokeStyle = '#fbf6ea'; c.lineWidth = Math.max(4, W2 / 200);
        for (let i = 0; i < 14; i++) { const a = i / 14 * Math.PI * 2, r0 = Math.min(W2, H2) * (0.12 + k * 0.5), r1 = r0 + Math.min(W2, H2) * 0.12; c.beginPath(); c.moveTo(it.x + Math.cos(a) * r0, it.y + Math.sin(a) * r0); c.lineTo(it.x + Math.cos(a) * r1, it.y + Math.sin(a) * r1); c.stroke(); }
        const fs = Math.round(Math.min(W2, H2) / 11); c.font = `700 ${fs}px ${I.FONT}`; c.textAlign = 'center'; c.lineJoin = 'round'; c.lineWidth = fs / 5; c.strokeStyle = '#fbf6ea'; c.strokeText('Encounter!', it.x, it.y - fs * 1.6); c.fillStyle = I.INK; c.fillText('Encounter!', it.x, it.y - fs * 1.6); c.restore();
      }
      if (k > 0.3) {   // ink blots grow from where you stand until the page is covered
        const p = (k - 0.3) / 0.7, R = Math.hypot(W2, H2) * p * 1.05;
        c.fillStyle = I.INK; c.beginPath();
        for (let i = 0; i < 9; i++) { const a = i / 9 * Math.PI * 2 + 0.3, d = R * 0.45; c.moveTo(it.x + Math.cos(a) * d + R * 0.62, it.y + Math.sin(a) * d); c.arc(it.x + Math.cos(a) * d, it.y + Math.sin(a) * d, R * 0.62, 0, Math.PI * 2); }
        c.fill();
      }
    } else {
      const k = (t - INTRO_MS) / INTRO_FADE_MS;   // the ink draws back, and you're in the arena
      c.clearRect(0, 0, W2, H2); c.fillStyle = `rgba(62,47,40,${1 - k})`; c.fillRect(0, 0, W2, H2);
    }
  }

  close(): void {
    if (!this.active) return;
    this.intro = null; $('battleFx').hidden = true;
    this.active = false; this.b = null; this.ending = null; this.menu = null; this.aim = null;
    $('battle').hidden = true; $('bMenu').hidden = true; document.body.classList.remove('in-battle');
    this.host.song('town');
    this.onClose();
  }

  // ---------- acting ----------
  private command(cmd: string, target?: number, skill?: string): void {
    if (!this.ready) { if (this.b && !this.ending) this.say('Your bar is still filling…', 700); return; }
    this.host.send(cmd, target, skill); this.openMenu(null);
  }
  private useBar(i: number): void {
    const id = this.barIds[i]; if (!id || !this.b || this.ending) return;
    if (id === 'attack') return this.command('attack', this.aim ?? undefined);
    const a = abilityDef(id)!, me = this.meF;
    if (!this.ready) { this.say('Your bar is still filling…', 700); return; }
    if (me && me.mp != null && me.mp < costFor(a, (me.job ?? 'knight') as JobId, me.lv)) { this.say(`Not enough MP for ${a.name}.`, 900); this.host.sound('error'); return; }
    this.cast(a);
  }
  /** Use an ability with sensible targets: creatures for attacks (the one you're aiming at), the friend who
   *  most needs it for a quick heal, yourself for a shield (the Skills list lets you pick a friend). */
  private cast(a: AbilityDef, pickAlly = false): void {
    const fs = this.b?.fighters ?? [];
    if (a.target === 'foe') return this.command('skill', this.aim ?? undefined, a.id);
    if (a.target === 'ally' && !pickAlly) {
      const hurt = a.heal ? fs.filter(f => f.side === 'party' && f.state !== 'down').sort((p, q) => p.hp / p.max - q.hp / q.max)[0] : undefined;
      return this.command('skill', hurt && hurt.hp < hurt.max ? hurt.id : this.host.me(), a.id);
    }
    if (a.target === 'ally') { this.pending = { cmd: 'skill', skill: a.id }; return this.openMenu('target-ally'); }
    if (a.target === 'fallen') { const down = fs.filter(f => f.side === 'party' && f.state === 'down'); if (down.length === 1) return this.command('skill', down[0].id, a.id); this.pending = { cmd: 'skill', skill: a.id }; return this.openMenu('target-fallen'); }
    this.command('skill', undefined, a.id);
  }

  private say(t: string, ms = 1300, warn = false): void { this.banner = t; this.bannerUntil = performance.now() + ms; this.bannerWarn = warn; }

  // ---------- lists: Skills, Items, picking a friend ----------
  private openMenu(which: BattleScreen['menu']): void {
    this.menu = which;
    const box = $('bMenuList'), title = $('bMenuTitle'), help = $('bHelpLine'), panel = $('bMenu');
    box.replaceChildren(); panel.hidden = !which;
    if (!which || !this.b) return;
    const btn = (label: string, on: () => void, disabled = false, hint = '', right = '') => {
      const b = document.createElement('button'); b.type = 'button'; b.disabled = disabled; b.dataset.help = hint;
      const l = document.createElement('span'); l.textContent = label; b.append(l);
      if (right) { const r = document.createElement('small'); r.textContent = right; b.append(r); }
      b.addEventListener('click', on);
      const show = () => { help.textContent = hint || '↑↓ choose · Enter OK · Esc back'; };
      b.addEventListener('focus', show); b.addEventListener('mouseenter', show);
      box.append(b); return b;
    };
    const me = this.meF, job = (me?.job ?? 'knight') as JobId;
    let first: HTMLButtonElement | null = null;
    if (which === 'skill') {
      title.textContent = `${MENU_NAME[job] ?? 'Skills'} · MP ${me?.mp ?? 0}`;
      for (const a of abilitiesFor(job, me?.lv ?? 1)) {
        const cd = me?.cd?.[a.id] ?? 0, cost = costFor(a, job, me?.lv ?? 1), noTarget = a.target === 'fallen' && !this.b.fighters.some(f => f.side === 'party' && f.state === 'down');
        const why = cd ? `Ready in ${cd} action${cd > 1 ? 's' : ''}.` : (me?.mp ?? 0) < cost ? 'Not enough MP.' : noTarget ? 'Nobody has fainted.' : '';
        const b = btn(a.name, () => this.cast(a, a.target === 'ally'), !!why, `${a.desc}${a.kind === 'spell' ? ' Stand still while it gathers.' : a.kind === 'weapon' ? ' You dash in to strike.' : ''}${why ? '  ' + why : ''}`, cd ? `⏳${cd}` : cost ? `${cost} MP` : '');
        b.classList.add('ab-' + a.kind); first ??= b.disabled ? null : b;
      }
    } else if (which === 'item') {
      title.textContent = 'Items';
      const items = this.host.items();
      if (!items.length) btn('Nothing to use', () => {}, true, 'Potions and ethers from your bag show up here.');
      for (const it of items) { const d = itemDef(it.def); const b = btn(d?.name ?? it.def, () => { this.pending = { cmd: 'item', skill: it.def }; this.openMenu('target-ally'); }, it.qty <= 0, d?.desc ?? '', `×${it.qty}`); first ??= b; }
    } else {
      const fs = this.b.fighters, list = which === 'target-foe' ? fs.filter(f => f.side === 'foe' && f.hp > 0) : which === 'target-ally' ? fs.filter(f => f.side === 'party' && f.state !== 'down') : fs.filter(f => f.side === 'party' && f.state === 'down');
      title.textContent = which === 'target-foe' ? 'Which creature?' : which === 'target-ally' ? 'On who?' : 'Bring back who?';
      for (const f of list) { const b = btn(f.name, () => this.command(this.pending!.cmd, f.id, this.pending!.skill), false, `${f.name}: ${f.hp}/${f.max} health.`, `${f.hp}/${f.max}`); first ??= b; }
    }
    btn('Back', () => this.openMenu(null), false, 'Close the list.');
    (first ?? box.firstElementChild as HTMLButtonElement | null)?.focus();
  }

  // ---------- the HUD ----------
  private barBtns: HTMLButtonElement[] = [];
  private barIds: string[] = [];
  private renderBar(): void {
    const box = $('bBar');
    const me0 = this.meF, ids = barFor((me0?.job ?? 'adventurer') as JobId);
    if (ids.join() !== this.barIds.join()) { this.barIds = ids; this.barBtns = []; box.replaceChildren(); }   // a new job: its own bar
    if (!this.barBtns.length) this.barBtns = this.barIds.map((id, i) => {
      const b = document.createElement('button'); b.type = 'button'; b.className = 'b-slot';
      const cv = document.createElement('canvas'); cv.width = 64; cv.height = 64; I.drawIcon(cv.getContext('2d')!, ICON[id] ?? ICON_FX[abilityDef(id)?.fx ?? ''] ?? 'sword', 32, 32, 20);
      const k = document.createElement('kbd'); k.textContent = String(i + 1);
      const n = document.createElement('span'); n.textContent = id === 'attack' ? 'Attack' : abilityDef(id)?.name ?? id;
      b.append(cv, k, n, document.createElement('small')); b.addEventListener('click', () => this.useBar(i)); box.append(b); return b;
    });
    const me = this.meF, job = (me?.job ?? 'knight') as JobId, lv = me?.lv ?? 1, ready = this.ready;
    this.barIds.forEach((id, i) => {
      const b = this.barBtns[i], a = abilityDef(id), cost = a ? costFor(a, job, lv) : 0, cd = me?.cd?.[id] ?? 0, short = !!a && (me?.mp ?? 0) < cost;
      const learned = !a || a.level <= lv;
      b.classList.toggle('ready', ready && !short && !cd && learned); b.classList.toggle('own', !!a && a.job === job); b.classList.toggle('locked', !learned); b.disabled = !!this.ending || !learned;
      b.querySelector('small')!.textContent = !learned ? `Lv ${a!.level}` : cd ? `⏳${cd}` : a ? (cost ? `${cost} MP` : '') : '';
      b.title = id === 'attack' ? 'Dash in and strike the creature you are aiming at.' : `${a!.desc}${a!.kind === 'spell' ? ' Stand still while it gathers.' : ''}${learned ? '' : ` Learned at ${jobDef(a!.job).name} level ${a!.level}.`}`;
    });
    $('bSkills').innerHTML = `${MENU_NAME[job] ?? 'Skills'} ▸ <kbd>Q</kbd>`;
  }
  private tools(): void {
    const b = this.b, me = this.meF; if (!b) return;
    $('bHelp').hidden = !!me?.helper; $('bHelp').textContent = b.help ? 'Help is on' : 'Call for help'; $('bHelp').classList.toggle('on', b.help);
    $('bAuto').textContent = me?.auto ? 'Auto: on' : 'Auto: off'; $('bAuto').classList.toggle('on', !!me?.auto);
    const solo = b.fighters.filter(f => f.side === 'party').length === 1;
    $('bWait').hidden = !solo; $('bWait').textContent = this.host.waitPref() ? 'Wait: on' : 'Wait: off'; $('bWait').classList.toggle('on', this.host.waitPref());
    $('bLeave').hidden = !me?.helper;
    const fd = formationDef(b.formation); $('bForm').textContent = `${fd.icon}  ${fd.name}${b.chain >= 2 ? `  ·  chain ×${b.chain}` : ''}`; $('bForm').title = fd.perks.join(' · ');
  }
  private panels(): void {
    const b = this.b; if (!b) return;
    const bar = (k: number, cls: string) => { const s = document.createElement('span'); s.className = cls; const i = document.createElement('i'); i.style.width = `${Math.round(Math.max(0, Math.min(1, k)) * 100)}%`; s.append(i); return s; };
    // the creature you're aiming at
    const t = b.fighters.find(f => f.id === this.aim), tgt = $('bTarget'); tgt.replaceChildren(); tgt.hidden = !t;
    if (t) {
      const n = document.createElement('b'); n.textContent = t.name; const lv = document.createElement('small'), con = conOf(t.lv, this.meF?.lv ?? 1); lv.textContent = `Lv ${t.lv} · ${con.name}${t.mob && MOBS[t.mob]?.boss ? ' · Boss' : ''}`; lv.style.color = con.id === 'even' ? '' : con.color;
      const head = document.createElement('div'); head.className = 'bt-head'; head.append(n, lv);
      tgt.append(head, bar(t.hp / t.max, 'bt-hp'), bar(t.atb / ATB_FULL, 'bt-atb'));
      if (t.charge) { const w = document.createElement('div'); w.className = 'b-charge'; w.textContent = `⚠ ${t.charge}`; tgt.append(w); }
      if (t.st?.length) { const st = document.createElement('div'); st.className = 'b-st'; st.textContent = t.st.map(([id]) => STATUS[id].icon).join(' '); tgt.append(st); }
    }
    // the party
    const party = b.fighters.filter(f => f.side === 'party');
    const shown = party.length <= 8 ? party : party.filter(f => f.id === this.host.me() || f.hp < f.max * 0.5).slice(0, 7);
    const rows: HTMLElement[] = shown.map(f => {
      const d = document.createElement('div'); d.className = 'b-row' + (f.state === 'down' ? ' down' : '') + (b.ready.includes(f.id) ? ' turn' : '') + (f.id === this.host.me() ? ' me' : '');
      const n = document.createElement('span'); n.className = 'b-name'; n.textContent = (f.state === 'defend' ? '🛡 ' : '') + f.name + (f.helper ? ' (helping)' : '') + (f.away ? ' (away)' : '');
      if (f.job) { const j = document.createElement('span'); j.className = 'b-job'; j.style.background = jobDef(f.job).color; j.textContent = jobDef(f.job).name.slice(0, 2) + f.lv; n.prepend(j); }
      if (f.auto) { const a = document.createElement('span'); a.className = 'b-auto'; a.textContent = 'AUTO'; n.append(a); }
      if (f.st?.length) { const st = document.createElement('span'); st.className = 'b-st'; st.textContent = f.st.map(([id]) => STATUS[id].icon).join(''); n.append(st); }
      const hp = document.createElement('span'); hp.className = 'b-hp' + (f.hp / f.max <= 0.25 ? ' low' : ''); hp.textContent = `${f.hp}/${f.max}`;
      d.append(n, hp, bar(f.atb / ATB_FULL, 'b-atb' + (f.atb >= ATB_FULL ? ' full' : '')));
      return d;
    });
    if (shown.length < party.length) { const more = document.createElement('div'); more.className = 'b-row b-more'; more.textContent = `+${party.length - shown.length} more`; rows.push(more); }
    $('bParty').replaceChildren(...rows);
    // you: health, magic, your bar (and a cast in progress)
    const me = this.meF, box = $('bMe'); box.replaceChildren();
    if (me) {
      const row = document.createElement('div'); row.className = 'bm-row';
      const nm = document.createElement('b'); nm.textContent = me.name; const nums = document.createElement('small'); nums.textContent = `♥ ${me.hp}/${me.max}${me.maxMp ? `  ✦ ${me.mp}/${me.maxMp}` : ''}`;
      row.append(nm, nums);
      const atb = bar(me.cast ? 1 - me.cast.left / Math.max(1, me.cast.total) : me.atb / ATB_FULL, 'bm-atb' + (this.ready ? ' full' : '') + (me.cast ? ' cast' : ''));
      const label = document.createElement('em'); label.textContent = me.cast ? `Casting ${me.cast.name}… stand still` : this.ready ? 'Ready!' : me.state === 'down' ? 'Fainted' : b.wait ? 'Waiting for you' : '';
      atb.append(label);
      box.append(row, bar(me.hp / me.max, 'bm-hp' + (me.hp / me.max <= 0.25 ? ' low' : '')), atb);
    }
  }

  // ---------- what happened ----------
  private play(e: BattleEvent): void {
    const h = this.host, name = (id: number) => this.b?.fighters.find(f => f.id === id)?.name ?? '';
    const foe = (id: number) => this.b?.fighters.find(f => f.id === id)?.side === 'foe';
    switch (e.k) {
      case 'use': {
        const a = abilityDef(e.skill);
        if (!e.from) this.say(e.name, 900);   // the arena's own: rocks, the ground
        else if (!foe(e.from) || a) this.say(`${name(e.from)}: ${e.name}`, 1000);
        if (a && !a.power && !a.heal && !a.revive && !a.steal) { for (const t of e.to) h.fx(t, a.fx); h.sound(a.status && STATUS[a.status.id].good ? 'buff' : 'debuff'); }
        if (!a) { const fx = fxOf(e.skill); if (fx) for (const t of e.to) h.fx(t, fx); }
        break;
      }
      case 'cast': { h.fx(e.who, 'steal'); h.sound('cast'); if (e.who === h.me()) this.say(`Casting ${e.name}… stand still`, 900); break; }
      case 'interrupt': this.say(`${name(e.who)}'s ${e.name} was interrupted!`, 1100); h.sound('error'); break;
      case 'dash': h.sound('dash'); break;
      case 'back': h.num(e.to, 'Back attack!', '#a8622a'); break;
      case 'push': h.shake(e.px >= 24 ? 3 : 1.6, 220); h.fx(e.who, 'bash'); break;
      case 'land': h.land(e.who); h.shake(6, 600); h.sound('error'); break;
      case 'hit': {
        const fx = fxOf(e.skill), a = abilityDef(e.skill);
        h.fx(e.to, fx ?? 'slash');
        if (e.burst) h.fx(e.to, 'burst');
        h.flash(e.to);
        h.num(e.to, e.burst || e.crit ? `${e.amt}!` : String(e.amt), e.burst ? '#d4a72c' : e.el ? EL_COLOR[e.el] : foe(e.to) ? (e.crit ? '#f28b4b' : '#e05a4c') : '#c9443a', e.burst || e.crit);
        if (e.crit) { h.num(e.to, 'Critical!', foe(e.to) ? '#f2a23a' : '#c9443a'); h.shake(foe(e.to) ? 1.4 : 2.4, 160); }
        if (e.weak) h.num(e.to, e.weak === 'weak' ? 'WEAK' : 'resist', e.weak === 'weak' ? '#e8743a' : '#8a8078');
        if (e.el && a?.kind === 'spell') h.sound(EL_SOUND[e.el]);
        h.sound(foe(e.to) ? 'hit' : 'hurt', e.burst ? 0.8 : 1);
        break;
      }
      case 'burst': this.say(`${e.name}  +${e.bonus}%`, 1500); h.sound('burst'); break;
      case 'status': {
        const def = STATUS[e.id];
        if (e.on) { h.num(e.to, def.name, def.good ? '#4f9ccc' : '#7a5aa8'); if (STATUS_FX[e.id]) h.fx(e.to, STATUS_FX[e.id]!); }
        else if (e.id === 'sleep') h.num(e.to, 'awake!', '#8a7a6c');
        break;
      }
      case 'tick': h.fx(e.who, e.id === 'poison' ? 'poison' : 'regen'); h.num(e.who, e.id === 'poison' ? String(e.amt) : `+${e.amt}`, e.id === 'poison' ? '#7a5aa8' : '#4f9a43'); h.sound(e.id === 'poison' ? 'poison' : 'heal'); break;
      case 'skip': this.say(e.why === 'sleep' ? `${name(e.who)} is fast asleep…` : `${name(e.who)} is stunned!`, 900); if (e.why === 'sleep') h.fx(e.who, 'sleep'); break;
      case 'steal': { const item = e.item ? itemDef(e.item)?.name ?? e.item : null; h.fx(e.to, 'steal'); this.say(item ? `${name(e.from)} stole ${item}!` : `${name(e.from)} came up empty.`, 1100); h.sound(item ? 'steal' : 'error'); break; }
      case 'revive': h.fx(e.to, 'revive'); h.num(e.to, `+${e.amt}`, '#4f9a43'); this.say(`${name(e.to)} is back on their feet!`, 1200); h.sound('holy'); break;
      case 'mp': h.num(e.who, `+${e.amt} MP`, '#3d6a8f'); break;
      case 'miss': h.num(e.to, 'miss', '#6f88c2'); h.sound('dodge'); break;
      case 'heal': h.fx(e.to, 'heal'); h.num(e.to, e.crit ? `+${e.amt}!` : `+${e.amt}`, '#4f9a43', e.crit); if (e.item) this.say(`${name(e.from)} used ${itemDef(e.item)?.name ?? 'a potion'}.`, 900); h.sound(e.item ? 'loot' : 'heal'); break;
      case 'defend': h.fx(e.who, 'protect'); break;
      case 'down': h.down(e.who); if (foe(e.who)) h.sound('poof'); break;
      case 'join': h.sound('join'); break;
      case 'escape': h.sound('dodge'); break;
      case 'say': this.say(e.text, 1400); break;
      case 'telegraph': this.say(`⚠ ${e.text}`, 2600, true); h.sound('error'); h.flash(e.from); break;
      case 'phase': this.say(e.text, 2400); h.sound('level'); h.flash(e.who); break;
      case 'chain': this.say(`${e.name}! ${e.n}-hit chain  +${e.bonus}%`, 1100); h.sound('chain', 1 + (e.n - 2) * 0.12); break;
      default: break;
    }
  }

  frame(now: number): void {
    if (!this.active) return;
    if (this.intro) this.drawIntro(now);
    if (this.ending && !this.resultShown && now - this.ending.at > 900) this.showResult();
    const show = !!this.banner && now < this.bannerUntil && !this.resultShown, bn = $('bBanner');
    if (bn.hidden === show) bn.hidden = !show;
    if (show && bn.textContent !== this.banner) bn.textContent = this.banner;
    bn.classList.toggle('warn', show && this.bannerWarn);
  }
}
